import { useEffect, useMemo, useState } from 'react'
import { orderBy } from 'lodash-es'
import { useContactInstrumentsInfiniteQuery } from 'hooks/queries/useContactInstrumentsInfiniteQuery'
import {
  BankAccountPaymentInstrument,
  ContactEntityCompany,
  ContactEntityIndividual,
  ContactPaymentInstruments,
  InstrumentType,
  RelationshipType,
} from 'types/contacts'
import {
  BeneficiaryEntityType,
} from 'types/beneficiaries'

interface WalletCapabilities {
  supportsWithdrawals: boolean
  supportsPay: boolean
  isB2cPayoutEnabled: boolean
}

export interface ContactBankAccountFilters {
  relationshipType?: RelationshipType
  currency?: string
  entityType?: BeneficiaryEntityType
  paymentMethods?: string[]
  beneficiarySearchTerm?: string
}

interface GetContactBankAccountsProps {
  filters: ContactBankAccountFilters
  capabilities: WalletCapabilities
}

const matchesCurrency = (fiatInstrument: ContactPaymentInstruments, currency?: string): boolean => {
  if (!currency) return true
  return fiatInstrument?.paymentInstrument?.currency === currency
}

const matchesPaymentMethod = (
  fiatInstrument: ContactPaymentInstruments,
  paymentMethods?: string[]
): boolean => {

  if (!paymentMethods?.length) return false

  const fiatInstrumentPaymentMethods = (fiatInstrument?.paymentInstrument as BankAccountPaymentInstrument)?.paymentMethods

  if (!fiatInstrumentPaymentMethods?.length) return false

  return paymentMethods?.some(paymentMethod => fiatInstrumentPaymentMethods?.includes(paymentMethod))
}

const matchesSearchTerm = (fiatInstrument: ContactPaymentInstruments, searchTerm?: string): boolean => {
  if (!searchTerm) return true

  const normalizedSearchTerm = searchTerm.toLowerCase()
  const searchableFields = [
    fiatInstrument?.contact?.name,
    fiatInstrument?.contact?.description,
    (fiatInstrument?.contact?.entity as ContactEntityIndividual)?.firstName,
    (fiatInstrument?.contact?.entity as ContactEntityIndividual)?.lastName,
    (fiatInstrument?.contact?.entity as ContactEntityCompany)?.legalName,
    fiatInstrument?.paymentInstrument?.description,
  ].filter(Boolean)
  return searchableFields.some(field => field?.toLowerCase().includes(normalizedSearchTerm))
}

const matchesEntityType = (
  fiatInstrument: ContactPaymentInstruments,
  entityType?: BeneficiaryEntityType,
  isB2cPayoutEnabled?: boolean
): boolean => {
  if (entityType) {

    if (entityType === BeneficiaryEntityType.BUSINESS) {
      return fiatInstrument?.contact?.entity?.type === BeneficiaryEntityType.COMPANY
    }


    return fiatInstrument?.contact?.entity?.type === entityType
  }

  // If B2C payouts are not enabled, exclude individual beneficiaries
  if (!isB2cPayoutEnabled && fiatInstrument?.contact?.entity?.type === BeneficiaryEntityType.INDIVIDUAL) {
    return false
  }

  return true
}

const matchesBeneficiaryType = (
  fiatInstrument: ContactPaymentInstruments,
  relationshipType?: RelationshipType,
  capabilities?: WalletCapabilities
): boolean => {
  const { supportsWithdrawals, supportsPay } = capabilities || {}

  if (relationshipType) {
    return fiatInstrument?.contact?.relationshipType === relationshipType
  }

  // If wallet supports neither withdrawals nor pay, show no beneficiaries
  if (!supportsWithdrawals && !supportsPay) return false

  // If wallet only supports pay, exclude first-party beneficiaries
  if (
    !supportsWithdrawals &&
    supportsPay &&
    fiatInstrument?.contact?.relationshipType === RelationshipType.SELF_OWNED
  ) {
    return false
  }

  // If wallet only supports withdrawals, exclude third-party beneficiaries
  if (
    supportsWithdrawals &&
    !supportsPay &&
    fiatInstrument?.contact?.relationshipType === RelationshipType.THIRD_PARTY
  ) {
    return false
  }

  return true
}

const filterContactBankAccounts = (
  contactBankAccounts: ContactPaymentInstruments[],
  filters: ContactBankAccountFilters,
  capabilities: WalletCapabilities
): ContactPaymentInstruments[] => {
  const { currency, entityType, paymentMethods, beneficiarySearchTerm, relationshipType } =
    filters

  return contactBankAccounts.filter(fiatInstrument => {
    const validCurrency = matchesCurrency(fiatInstrument, currency)
    const validPaymentMethod = matchesPaymentMethod(fiatInstrument, paymentMethods)
    const validSearchTerm = matchesSearchTerm(fiatInstrument, beneficiarySearchTerm)
    const validEntityType = matchesEntityType(fiatInstrument, entityType, capabilities?.isB2cPayoutEnabled)
    const validBeneficiaryType = matchesBeneficiaryType(fiatInstrument, relationshipType, capabilities)

    return (
      validCurrency &&
      validPaymentMethod &&
      validSearchTerm &&
      validEntityType &&
      validBeneficiaryType
    )
  })
}

export const useContactBankAccounts = ({
  filters,
  capabilities,
}: GetContactBankAccountsProps) => {
  const { currency, beneficiarySearchTerm } = filters || {}
  const [contactBankAccounts, setContactBankAccounts] = useState<
    ContactPaymentInstruments[] | undefined
  >()
  const [filteredContactPaymentInstruments, setFilteredContactPaymentInstruments] = useState<
    ContactPaymentInstruments[] | undefined
  >()

  const {
    data: contactInstruments,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useContactInstrumentsInfiniteQuery({
    size: 100,
    searchTerm: beneficiarySearchTerm,
    paymentMethod: InstrumentType.BANK_ACCOUNT,
    currency,
    enabled: !!currency,
  })

  useEffect(() => {
    if (!contactInstruments?.length) {
      setContactBankAccounts([])
      setFilteredContactPaymentInstruments([])
      return
    }

    // Apply filters
    const filtered = filterContactBankAccounts(contactInstruments, filters, capabilities)

    // Sort by business name, first name, last name, alias
    const sortedFiltered = orderBy(
      filtered,
      ['contact.name', 'contact.description', 'contact.entity.firstName', 'contact.entity.lastName', 'contact.entity.legalName', 'paymentInstrument.description'],
      ['asc', 'asc', 'asc', 'asc', 'asc', 'asc']
    )

    // Get the payment instruments that match the filtered beneficiaries
    const filteredPaymentInstruments = sortedFiltered
      .filter(Boolean) as ContactPaymentInstruments[]

    setContactBankAccounts(filteredPaymentInstruments)
    setFilteredContactPaymentInstruments(sortedFiltered)
  }, [contactInstruments, filters, capabilities])

  return useMemo(() => {
    return {
      contactBankAccounts,
      filteredContactPaymentInstruments,
      fetchNextPage,
      hasNextPage,
      isFetching: isPending || isFetchingNextPage,
      isFetchingNextPage,
      isError,
    }
  }, [
    contactBankAccounts,
    filteredContactPaymentInstruments,
    fetchNextPage,
    hasNextPage,
    isPending,
    isFetchingNextPage,
    isError,
  ])
}
